import { Translations } from '~/context/TranslationContext/models';
import { ItemListingCarousel, ItemTypes } from '~/mappers/itemListing/models';
import { ProductBrand } from '~/templates/L2ProductPage/models';

export enum PAGE_TYPE {
  RESOURCES = 'Resources',
  PRODUCTS = 'Products',
}

export const pageType: Record<PAGE_TYPE, string> = {
  [PAGE_TYPE.RESOURCES]: '/resources',
  [PAGE_TYPE.PRODUCTS]: '/product-list',
};

export const pageTypeName: Record<PAGE_TYPE, string> = {
  [PAGE_TYPE.RESOURCES]: 'Contentstack_resource_cards_by_category',
  [PAGE_TYPE.PRODUCTS]: 'Contentstack_product_cards_by_category',
};

export const pageLabel: Record<PAGE_TYPE, string> = {
  [PAGE_TYPE.RESOURCES]: 'Resources',
  [PAGE_TYPE.PRODUCTS]: 'Products',
};

const getButtonTitle = (t: Translations, type: PAGE_TYPE): string => {
  switch (type) {
    case PAGE_TYPE.RESOURCES:
      return t?.carousel?.resourcesButtonTitle;
    case PAGE_TYPE.PRODUCTS:
      return t?.carousel?.productButtonTitle;
    default:
      return 'See All';
  }
};

const getAriaLabel = (t: Translations, type: PAGE_TYPE): string => {
  switch (type) {
    case PAGE_TYPE.RESOURCES:
      return t?.carousel?.resourcesButtonAriaLabel;
    case PAGE_TYPE.PRODUCTS:
      return t?.carousel?.productButtonAriaLabel;
    default:
      return 'See All';
  }
};

const getHeadline = (t: Translations, type: PAGE_TYPE): string => {
  switch (type) {
    case PAGE_TYPE.RESOURCES:
      return t?.carousel?.relatedResources;
    case PAGE_TYPE.PRODUCTS:
      return t?.carousel?.relatedProducts;
    default:
      return 'Related Resources';
  }
};

const showMoreButtonResources = (
  page_Type: PAGE_TYPE,
  t: Translations,
  seeMorePageUrl: string
) => {
  const url =
    seeMorePageUrl && seeMorePageUrl !== '/'
      ? seeMorePageUrl
      : pageType[page_Type];

  return {
    variant: 'primary',
    size: 'large',
    link: [
      {
        url,
        aria_label: getAriaLabel(t, page_Type),
        title: getButtonTitle(t, page_Type),
        fields: {
          fullPath: `${url}`,
        },
      },
    ],
    title: getButtonTitle(t, page_Type),
    aria_label: getAriaLabel(t, page_Type),
    iconPrefix: [],
    iconSuffix: [],
  };
};

export const createItemListingCarouselMock = (
  isMobile: boolean,
  t: Translations,
  page_type: PAGE_TYPE,
  productBrand?: [ProductBrand]
): ItemListingCarousel => {
  const filters = productBrand?.[0]?.allProductsConfig?.filters ?? '';
  const fullPath =
    productBrand?.[0]?.allProductsConfig?.page[0]?.fields?.fullPath ?? '';

  const seeMorePageUrl = `${fullPath}/${filters}`.replace(/\/{2,}/g, '/');

  const showMoreButton =
    page_type === PAGE_TYPE.RESOURCES || page_type === PAGE_TYPE.PRODUCTS
      ? [showMoreButtonResources(page_type, t, seeMorePageUrl)]
      : [];

  return {
    typeName: 'Contentstack_item_listing_carousel',
    uid: `carousel-uid-${page_type.toLocaleLowerCase()}`,
    componentId: `related-${page_type.toLocaleLowerCase()}-carousel-listing`,
    carousel: [
      {
        ui: {
          variant: 'fixed',
          progressIndicator: 'rails',
          preferredCardSize: isMobile ? 300 : 400,
          gap: 16,
          ariaLivePlaceholder: '{index} of {total}',
          buttonNext: {
            disabled: false,
            size: 'small',
            text: '',
            variant: 'primary-icon',
            ariaLabel: `Next ${page_type}`,
            iconPrefix: [{ icon: 'arrowRight' }],
            iconSuffix: [],
          },
          buttonPrevious: {
            disabled: false,
            size: 'small',
            text: '',
            variant: 'primary-icon',
            ariaLabel: `Previous ${page_type}`,
            iconPrefix: [{ icon: 'arrowLeft' }],
            iconSuffix: [],
          },
          numbered: false,
          centerInsufficientSlides: true,
        },
      },
    ],
    pageBlock: {
      heading: {
        headline: getHeadline(t, page_type),
        descriptionRte: '',
        detail: '',
        button: null,
        link: [],
        ui: [],
      },
      disableGutters: false,
      element: 'div',
      mode: 'default',
      maxWidth: 'max-grid',
    },
    items: [
      {
        typeName: pageTypeName[page_type],
        variant: 'standard',
        mode: 'horizontal',
        align: 'center',
        horizontalDirection: 'row',
        imageSettings: {
          aspectRatio: '',
          objectPosition: '',
          objectFit: page_type === pageLabel.Resources ? 'cover' : 'contain',
          rounded: false,
        },
        cardContentUI: [
          {
            ui: {
              align: 'start',
              wrapping: 'default',
              headlineTag: 'h3',
              headlineVariant: 'headline',
              headlineSize: 's',
              headlineWeight: 'bold',
              descriptionVariant: 'body',
              descriptionSize: 'm',
              descriptionWeight: 'regular',
              detailVariant: 'detail',
              detailSize: 'm',
              detailWeight: 'regular',
            },
          },
        ],
        categories: [],
        items: [],
        showLabels: true,
        showCategory: true,
        descriptionField: 'page data',
      },
    ],
    showMoreButton,
  };
};

export const mapPageDataToCarouselItem = (page) => {
  const listImageObj = page.use_fallback_image
    ? { ...page.listing_image[0] }
    : { ...page.pageHeader.image_element[0] };

  return {
    relatedPageUid: page.uid,
    label: page.listing_data.label || [],
    heading: {
      headline: page.pageHeader.headingBlock.headline,
      descriptionRte:
        page.listing_data.description ||
        page.pageHeader.headingBlock.descriptionRte,
      detail: '',
      button: [],
      link: [],
      ui: [],
    },
    image: {
      ...listImageObj,
    },
    listingData: {
      label: [...page.listing_data.label],
      description: page.listing_data.description,
    },
    fullPath: page.fields.fullPath,
    categories: page.categories,
    updatedAt: page.updated_at,
    pageAdditionalData: { ...page.page_additional_data },
    eventTimestamp: { ...page.event_timestamp },
  };
};

export const mapProductPageDataToCarouselItem = (page): ItemTypes => {
  const heroImage = {
    url: page?.local_pim_data[0]?.hero_image?.url,
    alt: 'Image',
    localAsset: {
      childImageSharp: page?.local_pim_data[0]?.hero_image?.gatsbyImageData,
    },
  };

  const listingData = {
    label: [],
    description:
      page?.local_pim_data[0]?.description_short ||
      page?.local_pim_data[0]?.description_long,
  };

  const listingImage = [
    {
      alt: '',
      image: { url: '' },
    },
  ] as [{ alt: string; image: { url: string } }];

  const productData = {
    heroImage,
    productTitle: page?.local_pim_data[0]?.product_title,
    descriptionShort: page?.local_pim_data[0]?.description_short,
    descriptionLong: page?.local_pim_data[0]?.description_long,
    aromaScent: page?.local_pim_data[0]?.aroma,
    barcodeNumber: page?.local_pim_data[0]?.barcode_number,
    productFeatures: [],
    brandName: [],
    segmentDescription: '',
    netContentStatement: '',
    variantAndIcon: {
      variantName: '',
      variantIcon: heroImage,
    },
  };

  return {
    relatedPageUid: page.uid,
    fullPath: page.fields.fullPath,
    updatedAt: page.updated_at,
    productData,
    listingData,
    categories: page?.categories,
    productBrand: page?.productBrand,
    useFallbackImage: false,
    listingImage,
  };
};
